import React, { useState, useEffect } from "react";
import { Table, FormControl } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import uuid from "react-uuid";
import { useTranslation } from "react-i18next";
import { useDebounce } from "use-debounce";
import "../../i18n";
import DatasetSelector from "../../components/Dataset/DatasetSelector";
import Loader from "../../components/Ui/Loader";
import PaginationComponent from "../../components/Ui/Pagination";
import CookieManager from "../../components/Cookies/CookieManager";
import "./datasets.scss";
import Dashboard from "../../layout/Dashboard/Dashboard";
import line from "../../assets/images/line.png";
import { useDatasets } from "../../hooks/useDatasets";
import useSelectionStore from "../../store/useSelectionStore";

const Datasets = () => {
  const { t } = useTranslation();
  const { getSpecificDataset } = useDatasets();
  const [selectedUnicDataset, setSelectedUnicDataset] = useState(null);
  const [theadList, setTheadList] = useState([]);
  const [tbodyList, setTbodyList] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [showMetaData, setShowMetaData] = useState(false);
  const itemsPerPage = 25;
  const meteDataID = [
    "67249b4e5f6c8c6aa07b8a68",
    "67249d437df43da6cfacf571",
    "67249d49cbc750ab04f15e60",
    "67249d4f09d001cf5a2ecc12",
    "67249d557831c5ee09e28e8a",
    "67249d59832d993813a07142",
  ];
  const { selectedDataset, categoriUpdate, resetSelection } =
    useSelectionStore();
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
  const [dataSource, setDataSource] = useState([""]);
  const [datasetDescription, setDatasetDescription] = useState("");
  const location = useLocation();

  useEffect(() => {
    setShowTable(false);
    setIsTableLoading(false);
    setDatasetDescription("");
    setDataSource([""]);
  }, [categoriUpdate]);

  // Reset the table when the component mounts
  useEffect(() => {
    resetSelection(); // Clear selection when user navigates to this page
  }, [location.pathname]);

  useEffect(() => {
    if (selectedDataset) {
      setIsTableLoading(true);

      const fetchDatasetWithId = async () => {
        try {
          const data = await getSpecificDataset(selectedDataset);
          setSelectedUnicDataset(data);
          setDataSource(data.source);
          setDatasetDescription(data.description);
          if (
            meteDataID.includes(selectedDataset) ||
            selectedDataset === "67d2ded2e0624309df0ec50d"
          ) {
            setShowMetaData(true);
          } else {
            setShowMetaData(false);
          }

          setError(null);
        } catch (error) {
          console.error("Error fetching dataset", error);
          setError("Failed to load dataset. Please try again.");
        } finally {
          setIsTableLoading(false);
        }
      };
      fetchDatasetWithId();
    }
  }, [selectedDataset]);

  useEffect(() => {
    if (selectedUnicDataset && selectedUnicDataset.elements.length > 0) {
      setTheadList(Object.keys(selectedUnicDataset.elements[0]));
      setTbodyList(
        selectedUnicDataset.elements.map((item) => Object.values(item))
      );
      setShowTable(true);
      setCurrentPage(1);
    }
    // eslint-disable-next-line
  }, [selectedUnicDataset]);

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredData = tbodyList.filter((row) =>
    row.some((value) =>
      value
        ?.toString()
        .toLowerCase()
        .includes(debouncedSearchQuery.toLowerCase())
    )
  );

  // Handle sorting
  const handleSort = (columnIndex) => {
    let direction = "ascending";
    if (
      sortConfig?.key === columnIndex &&
      sortConfig.direction === "ascending"
    ) {
      direction = "descending";
    }
    const sortedData = [...tbodyList].sort((a, b) => {
      const valueA = a[columnIndex];
      const valueB = b[columnIndex];

      if (typeof valueA === "number" && typeof valueB === "number") {
        return direction === "ascending" ? valueA - valueB : valueB - valueA;
      } else {
        return direction === "ascending"
          ? String(valueA).localeCompare(String(valueB))
          : String(valueB).localeCompare(String(valueA));
      }
    });

    setSortConfig({ key: columnIndex, direction });
    setTbodyList(sortedData);
  };

  // Handle pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // handel dataset download
  const handleDownload = () => {
    const fileData = JSON.stringify(filteredData, null, 2);
    const blob = new Blob([fileData], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const filename = `${selectedUnicDataset.name || "data"}`.replace(
      /[^a-z0-9_\-\.]/gi,
      "_"
    );
    link.download = `${filename}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <CookieManager />
      <Dashboard>
        <div>
          <h2 className="datasetsHeader">{t("datasetHeader")}</h2>
          <p className="datasetsDesc">{t("datasetDescription")}</p>
          <img className="separator" src={line} alt="" />
          <DatasetSelector />
          {error && <p className="error-text">{error}</p>}

          {isTableLoading ? (
            <Loader
              text="Please wait... Loading ... Do not forget to select a dataset."
              animationSize={300}
              containerHeight={300}
            />
          ) : (
            <>
              {selectedDataset && showTable && (
                <div className="row">
                  <div className="col-m-6 px-5 pl-22 d-flex justify-content-start align-items-center">
                    {showMetaData && (
                      <button
                        type="submit"
                        className="custom-btn fw-bold me-3"
                        onClick={() => {
                          let metadataId;

                          if (meteDataID.includes(selectedDataset)) {
                            metadataId = "6724a32123b5295d86a34483";
                          } else if (
                            selectedDataset === "67d2ded2e0624309df0ec50d"
                          ) {
                            metadataId = "67d2dd176bf1459221215df7";
                          } else {
                            console.error(
                              "No metadata ID found for the selected dataset."
                            );
                            return;
                          }

                          window.open(`/meta-data?id=${metadataId}`, "_blank");
                        }}
                      >
                        Meta Data
                      </button>
                    )}
                    <button
                      className="custom-btn fw-bold"
                      onClick={handleDownload}
                    >
                      Download Data
                    </button>
                  </div>
                </div>
              )}

              <div className="datasetsTable" hidden={!showTable}>
                <FormControl
                  type="text"
                  placeholder="Search"
                  className="mb-3"
                  value={searchQuery}
                  onChange={handleSearch}
                />
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      {theadList.map((item, index) => (
                        <th key={uuid()} onClick={() => handleSort(index)}>
                          {item.replace("?", "")}{" "}
                          {sortConfig?.key === index
                            ? sortConfig.direction === "ascending"
                              ? "▲"
                              : "▼"
                            : null}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((rowData) => (
                      <tr key={uuid()}>
                        {rowData.map((value) => (
                          <td key={uuid()}>{value}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <PaginationComponent
                  totalItems={filteredData.length}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={paginate}
                />
              </div>
            </>
          )}
        </div>
        {selectedDataset && dataSource.length > 0 && datasetDescription && (
          <div className="dataset-info">
            <div className="info-row">
              <span className="info-label">Source: </span>
              <span>{dataSource}.</span>
            </div>
            <div className="info-row">
              <span className="info-label">Description: </span>
              <span>{datasetDescription}.</span>
            </div>
          </div>
        )}
      </Dashboard>
    </>
  );
};

export default Datasets;
